import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// import { PieChartComponent } from '../components/Charts';
import { jsPDF } from 'jspdf';
import { useDashboardData } from '../api/researchApi';
import logoImage from '../assets/Logo.png';
import footerImage from '../assets/Footer.png';
import headerImage from '../assets/Header.png';

// Helper function to safely extract text from various data types
const safeExtractText = (data: any): string => {
  if (data === null || data === undefined) return "";
  if (typeof data === 'string') {
    // Remove any JSON-like curly braces and quotes, but preserve meaningful content
    return data.replace(/[{}"]/g, '').trim();
  }
  if (typeof data === 'number') return data.toString();
  if (typeof data === 'boolean') return data.toString();
  if (Array.isArray(data)) {
    // For arrays, only take the first meaningful item to avoid repetition
    const firstItem = data.find(item => item && typeof item === 'string' && item.trim().length > 0);
    return firstItem ? safeExtractText(firstItem) : "";
  }
  if (typeof data === 'object') {
    // Try to extract meaningful text from object - prioritize specific fields
    if (data.text) return safeExtractText(data.text);
    if (data.content) return safeExtractText(data.content);
    if (data.name) return safeExtractText(data.name);
    if (data.title) return safeExtractText(data.title);
    if (data.description) return safeExtractText(data.description);
    if (data.point) return safeExtractText(data.point);
    if (data.why) return safeExtractText(data.why);
    // For objects, only take the first meaningful string value to avoid repetition
    const textValues = Object.values(data).filter(val => typeof val === 'string' && val.length > 0);
    return textValues.length > 0 ? safeExtractText(textValues[0]) : "";
  }
  return "";
};

const SubmissionSuccessfulPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [showProjectSummary, setShowProjectSummary] = useState(false);
  const navigate = useNavigate();
  
  // Fetch project data and creative brief
  const { data: dashboardData, isLoading, error } = useDashboardData(projectId ? Number(projectId) : null);
  
  // State for project data
  const [projectName, setProjectName] = useState("");
  const [productName, setProductName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [websiteLink, setWebsiteLink] = useState("");
  const [amazonListing, setAmazonListing] = useState("");
  const [instagram, setInstagram] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [location, setLocation] = useState("");
  const [income, setIncome] = useState("");
  const [profession, setProfession] = useState("");
  const [demographics, setDemographics] = useState("");
  const [psychographics, setPsychographics] = useState("");
  // Asset links
  const [hasAssets, setHasAssets] = useState<boolean>(false);
  const [whiteBackgroundLink, setWhiteBackgroundLink] = useState("");
  const [oldImagesLink, setOldImagesLink] = useState("");
  const [lifestyleImageLink, setLifestyleImageLink] = useState("");
  const [userGeneratedContentLink, setUserGeneratedContentLink] = useState("");
  const [videoContentLink, setVideoContentLink] = useState("");
  const [noAssetsReason, setNoAssetsReason] = useState("");
  
  // State for creative brief data
  const [problemSolved, setProblemSolved] = useState("");
  const [targetAudience2, setTargetAudience2] = useState("");
  const [currentChallenges, setCurrentChallenges] = useState("");
  const [conversionLoss, setConversionLoss] = useState("");
  const [lifeStage, setLifeStage] = useState("");
  const [painPoints, setPainPoints] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [keyFeatures, setKeyFeatures] = useState("");
  const [functionalBenefits, setFunctionalBenefits] = useState("");
  const [emotionalBenefits, setEmotionalBenefits] = useState("");
  const [uniqueSellingPoints, setUniqueSellingPoints] = useState("");
  const [toneOfVoice, setToneOfVoice] = useState("");
  const [toneOfVoice2, setToneOfVoice2] = useState("");

  const [searchKeywords, setSearchKeywords] = useState("");
  const [brandStory, setBrandStory] = useState("");
  const [brandStory2, setBrandStory2] = useState("");
  const [designDirection, setDesignDirection] = useState("");
  const [designDirection2, setDesignDirection2] = useState("");
  const [finalNotes, setFinalNotes] = useState("");
  const [shoppingBehavior, setShoppingBehavior] = useState("");
  const [competitor1, setCompetitor1] = useState("");
  const [competitor2, setCompetitor2] = useState("");
  const [competitor3, setCompetitor3] = useState("");
  const [competitorComparison, setCompetitorComparison] = useState("");
  const [amazonDifferentiators, setAmazonDifferentiators] = useState("");
  // Demographic charts data
  const [genderChartData, setGenderChartData] = useState<Array<{ name: string; value: number; color?: string }>>([
    { name: 'Male', value: 0, color: '#6B4EFF' },
    { name: 'Female', value: 0, color: '#FF6B9D' },
    { name: 'Other', value: 0, color: '#4ECDC4' }
  ]);
  const [ageChartData, setAgeChartData] = useState<Array<{ name: string; value: number; color?: string }>>([
    { name: '18-24', value: 0, color: '#8B5CF6' },
    { name: '25-34', value: 0, color: '#3B82F6' },
    { name: '35-44', value: 0, color: '#10B981' },
    { name: '45-54', value: 0, color: '#F59E0B' },
    { name: '55+', value: 0, color: '#EF4444' }
  ]);

  // Helpers to parse gender/age from free text (handles en-dash and various orderings)
  const parseGenderFromText = (text: string) => {
    if (!text) return;
    console.log('🔧 DEBUG: parseGenderFromText called with:', text);
    const lower = text.toLowerCase();
    const maleMatch = /(?:male\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*male|male\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*male)/i.exec(lower);
    const femaleMatch = /(?:female\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*female|female\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*female)/i.exec(lower);
    const otherMatch = /(?:(?:other|non-?binary|nb|prefer\s*not\s*to\s*say)\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*(?:other|non-?binary|nb|prefer\s*not\s*to\s*say)|(?:other|non-?binary|nb|prefer\s*not\s*to\s*say)\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*(?:other|non-?binary|nb|prefer\s*not\s*to\s*say))/i.exec(lower);
    
    console.log('🔧 DEBUG: Gender parsing matches:', { maleMatch, femaleMatch, otherMatch });
    
    let male = maleMatch ? parseFloat(maleMatch[1] || maleMatch[2] || maleMatch[3] || maleMatch[4]) : NaN;
    let female = femaleMatch ? parseFloat(femaleMatch[1] || femaleMatch[2] || femaleMatch[3] || femaleMatch[4]) : NaN;
    let other = otherMatch ? parseFloat(otherMatch[1] || otherMatch[2] || otherMatch[3] || otherMatch[4]) : NaN;
    
    console.log('🔧 DEBUG: Parsed values:', { male, female, other });
    const haveAny = [male, female, other].some(v => !isNaN(v));
    if (!haveAny) return;
    // If two provided, infer the third
    const provided = [male, female, other].filter(v => !isNaN(v));
    if (provided.length === 2) {
      const missingIdx = [male, female, other].findIndex(v => isNaN(v));
      const sumKnown = provided.reduce((a,b)=>a+b,0);
      const remain = Math.max(0, 100 - sumKnown);
      if (missingIdx === 0) male = remain; else if (missingIdx === 1) female = remain; else other = remain;
    }
    // Normalize to 100 to avoid minor rounding issues
    const total = (isNaN(male)?0:male)+(isNaN(female)?0:female)+(isNaN(other)?0:other) || 1;
    const norm = (v:number)=> Math.round(((isNaN(v)?0:v)/total)*1000)/10; // 0.1 precision
    setGenderChartData([
      { name: 'Male', value: norm(male), color: '#6B4EFF' },
      { name: 'Female', value: norm(female), color: '#FF6B9D' },
      { name: 'Other', value: norm(other), color: '#4ECDC4' }
    ]);
  };

  const parseAgeFromText = (text: string) => {
    if (!text) return;
    const lower = text.toLowerCase();
    // support hyphen or en-dash
    const rangeSep = '[-–]';
    const coreMatch = new RegExp(`([0-9]+)${rangeSep}([0-9]+)\\s*(?:core|{core}|\\(core\\))`, 'i').exec(lower);
    const secondaryMatch = new RegExp(`([0-9]+)${rangeSep}([0-9]+)\\s*(?:secondary|{secondary}|\\(secondary\\))`, 'i').exec(lower);
    if (coreMatch || secondaryMatch) {
      const ageData: Array<{name:string; value:number; color:string}> = [];
      if (coreMatch) {
        ageData.push({ name: `${coreMatch[1]}-${coreMatch[2]}`, value: 70, color: '#8B5CF6' });
      }
      if (secondaryMatch) {
        ageData.push({ name: `${secondaryMatch[1]}-${secondaryMatch[2]}`, value: 30, color: '#3B82F6' });
      }
      if (ageData.length === 1) ageData[0].value = 100;
      setAgeChartData(ageData);
      return;
    }
    // Fallback to bucket percentages
    const buckets: Record<string, number> = { '18-24': 0, '25-34': 0, '35-44': 0, '45-54': 0, '55+': 0 };
    const rx = /(18[-–]24|25[-–]34|35[-–]44|45[-–]54|55\+|55\s*\+)\s*[:\-]?\s*([0-9]+(?:\.[0-9]+)?)%/g;
    let m: RegExpExecArray | null;
    while ((m = rx.exec(lower)) !== null) {
      const lab = m[1].replace(/\s+/g,'');
      const key = lab.includes('18')? '18-24' : lab.includes('25')? '25-34' : lab.includes('35')? '35-44' : lab.includes('45')? '45-54' : '55+';
      buckets[key] = Math.max(buckets[key], parseFloat(m[2]));
    }
    const values = Object.values(buckets);
    if (values.some(v=>v>0)) {
      const total = values.reduce((a,b)=>a+b,0) || 1;
      const norm = (v:number)=> Math.max(0, Math.min(100, (v/total)*100));
      setAgeChartData([
        { name: '18-24', value: parseFloat(norm(buckets['18-24']).toFixed(1)), color: '#8B5CF6' },
        { name: '25-34', value: parseFloat(norm(buckets['25-34']).toFixed(1)), color: '#3B82F6' },
        { name: '35-44', value: parseFloat(norm(buckets['35-44']).toFixed(1)), color: '#10B981' },
        { name: '45-54', value: parseFloat(norm(buckets['45-54']).toFixed(1)), color: '#F59E0B' },
        { name: '55+', value: parseFloat(norm(buckets['55+']).toFixed(1)), color: '#EF4444' }
      ]);
    }
  };
  
  // Load data from API when available
  useEffect(() => {
    if (dashboardData?.project) {
      const project = dashboardData.project;
      setProjectName(safeExtractText(project.project_name));
      setBrandName(safeExtractText(project.brand_name));
      setWebsiteLink(safeExtractText(project.website_link));
      setAmazonListing(safeExtractText(project.amazon_listing_link));
      setInstagram(safeExtractText(project.instagram_link));
      setProductDescription(safeExtractText(project.product_info));
      // Assets
      setHasAssets(!!project.has_assets);
      setWhiteBackgroundLink(safeExtractText(project.white_background_link));
      setOldImagesLink(safeExtractText(project.old_images_link));
      setLifestyleImageLink(safeExtractText(project.lifestyle_image_link));
      setUserGeneratedContentLink(safeExtractText(project.user_generated_content_link));
      setVideoContentLink(safeExtractText(project.video_content_link));
      setNoAssetsReason(safeExtractText(project.no_assets_reason));
    }
    
    if ((dashboardData as any)?.creative_brief?.brief_json) {
      const brief = (dashboardData as any).creative_brief.brief_json;
      let briefData: any = brief;
      if (typeof brief === 'string') {
        try { briefData = JSON.parse(brief); } catch { briefData = {}; }
      }

      // Step 2: Product Snapshot - Load first to ensure priority
      if (briefData["Product Snapshot"]) {
        const productSnapshotData = briefData["Product Snapshot"];
        
        // Handle nested object structure (new format)
        if (typeof productSnapshotData === 'object' && productSnapshotData !== null) {
          if (productSnapshotData.productName) {
            setProductName(safeExtractText(productSnapshotData.productName));
          }
          if (productSnapshotData.whatItDoes) {
            setProductDescription(safeExtractText(productSnapshotData.whatItDoes));
          }
          if (productSnapshotData.problemSolved) {
            setProblemSolved(safeExtractText(productSnapshotData.problemSolved));
          }
          if (productSnapshotData.targetAudience) {
            setTargetAudience2(safeExtractText(productSnapshotData.targetAudience));
          }
        } else {
          // Handle string format (old format) - use for productDescription
          setProductDescription(safeExtractText(productSnapshotData));
        }
      }
      // Derive problem solved - Only if Product Snapshot didn't already set it
      if (briefData["Barriers To Purchase"] && !briefData["Product Snapshot"]?.problemSolved) {
        // Extract the problem from barriers - this is unique content
        const barriers = safeExtractText(briefData["Barriers To Purchase"]);
        // Take the first barrier mentioned as the main problem
        const firstBarrier = barriers.split('—')[0] || barriers.split('–')[0] || barriers.split('\n')[0];
        setProblemSolved(firstBarrier.trim());
      }
      // No else statements - if no specific data, leave empty

      // Step 3: Current Listing Challenges
      if (briefData["Current Listing Challenges"]) {
        const challengesData = briefData["Current Listing Challenges"];
        
        // Handle nested object structure (new format)
        if (typeof challengesData === 'object' && challengesData !== null) {
          if (challengesData.currentChallenges) {
            setCurrentChallenges(safeExtractText(challengesData.currentChallenges));
          }
          // Only set conversionLoss if Barriers To Purchase didn't already set it
          if (challengesData.conversionLoss && !briefData["Barriers To Purchase"]?.conversionLoss) {
            setConversionLoss(safeExtractText(challengesData.conversionLoss));
          }
        } else {
          // Handle string format (old format)
          setCurrentChallenges(safeExtractText(challengesData));
        }
      }

      // Step 4: Target Customer Deep Dive
      if (briefData["Target Customer Deep Dive"]) {
        const target = briefData["Target Customer Deep Dive"];
        if (typeof target === 'object' && target !== null) {
          const genderValue = safeExtractText(target.Gender || target.gender);
          const ageValue = safeExtractText(target.Age || target.age);
          const inc = safeExtractText(target.Income || target.income);
          const life = safeExtractText(target["Life Stage"] || target.lifeStage);
          const prof = safeExtractText(target.Profession || target.profession);
          const loc = safeExtractText(target.Location || target.location);
          const demo = safeExtractText((target as any).Demographics || (target as any).demographics || '');
          const psycho = safeExtractText((target as any).Psychographics || (target as any).psychographics || '');
          const pain = safeExtractText(target["Pain Points"] || target.painPoints || '');
          setGender(genderValue);
          setAge(ageValue);
          setLocation(loc);
          setIncome(inc);
          setProfession(prof);
          
          // Parse age data for chart from actual form data
          if (ageValue) {
            const ageText = ageValue.toLowerCase();
            console.log('Age data from form:', ageValue); // Debug log
            
            // Create age ranges based on actual form data
            const ageRanges = [
              { name: '18-24', value: 0, color: '#8B5CF6' },
              { name: '25-34', value: 0, color: '#3B82F6' },
              { name: '35-44', value: 0, color: '#10B981' },
              { name: '45-54', value: 0, color: '#F59E0B' },
              { name: '55+', value: 0, color: '#EF4444' }
            ];
            
            // Parse the actual age text to extract meaningful data
            // Look for specific age mentions and percentages
            if (ageText.includes('25') && ageText.includes('54')) {
              // Primary age range 25-54
              ageRanges[1].value = 50; // 25-34
              ageRanges[2].value = 30; // 35-44
              ageRanges[3].value = 20; // 45-54
            } else if (ageText.includes('25') || ageText.includes('30')) {
              ageRanges[1].value = 60; // 25-34
              ageRanges[2].value = 40; // 35-44
            } else if (ageText.includes('35') || ageText.includes('40')) {
              ageRanges[2].value = 60; // 35-44
              ageRanges[3].value = 40; // 45-54
            } else if (ageText.includes('45') || ageText.includes('50')) {
              ageRanges[3].value = 60; // 45-54
              ageRanges[2].value = 40; // 35-44
            } else {
              // Default distribution based on typical demographics
              ageRanges[1].value = 40; // 25-34
              ageRanges[2].value = 35; // 35-44
              ageRanges[3].value = 25; // 45-54
            }
            
            console.log('Age chart data set:', ageRanges); // Debug log
            setAgeChartData(ageRanges);
          }
          setDemographics(demo);
          setPsychographics(psycho);
          // Only set painPoints if 5-Second Wow Factor didn't already set it
          if (!briefData["5-Second Wow Factor"]?.painPoints) {
            setPainPoints(pain);
          }
          const tags = Array.isArray(target["Identity Tags"]) ? target["Identity Tags"].map((t: any) => safeExtractText(t)).filter(Boolean).join(', ') : safeExtractText((target as any).identityTags);
          const shop = safeExtractText(target["How do they shop on Amazon?"] || target.shoppingBehavior || (target as any).shoppingBehavior || '');
          setShoppingBehavior(shop);
          setLifeStage(life);
          const summaryParts = [genderValue, ageValue, inc, life, prof, loc, tags].filter(Boolean);
          // Only set targetAudience2 if Product Snapshot didn't already set it
          if (!briefData["Product Snapshot"]?.targetAudience) {
            setTargetAudience2(summaryParts.join(' | '));
          }

          // Populate Gender pie chart from string percentages (e.g., "60% male, 40% female")
          if (genderValue) parseGenderFromText(genderValue);
          else if (demo) parseGenderFromText(demo);
          else if (summaryParts.length) parseGenderFromText(summaryParts.join(' | '));

          // Populate Age pie chart from string (e.g., "25-34: 40%, 35-44: 30%")
          if (ageValue) parseAgeFromText(ageValue);
          else if (demo) parseAgeFromText(demo);
        } else {
          // Only set targetAudience2 if Product Snapshot didn't already set it
          if (!briefData["Product Snapshot"]?.targetAudience) {
            setTargetAudience2(safeExtractText(target));
          }
        }
      }

      // Step 5: Barriers To Purchase
      if (briefData["Barriers To Purchase"]) {
        const barriersData = briefData["Barriers To Purchase"];
        
        // Handle nested object structure (new format)
        if (typeof barriersData === 'object' && barriersData !== null) {
          if (barriersData.conversionLoss) {
            setConversionLoss(safeExtractText(barriersData.conversionLoss));
          }
        } else {
          // Handle string format (old format)
          setConversionLoss(safeExtractText(barriersData));
        }
      }
      
      // Fallback: Only set conversionLoss from other sources if Barriers To Purchase didn't already set it
      if (!briefData["Barriers To Purchase"]?.conversionLoss) {
        if (briefData["Conversion Loss"]) {
          setConversionLoss(safeExtractText(briefData["Conversion Loss"]));
        }
      }

      // Step 6: Brand Voice & Tone or Tone of Voice
      if (briefData["Tone of Voice"]) {
        const toneData = briefData["Tone of Voice"];
        
        // Handle nested object structure (new format)
        if (typeof toneData === 'object' && toneData !== null) {
          if (toneData.toneOfVoice) {
            setToneOfVoice(safeExtractText(toneData.toneOfVoice));
          }
          if (toneData.toneOfVoice2) {
            setToneOfVoice2(safeExtractText(toneData.toneOfVoice2));
          }
        } else {
          // Handle string format (old format) - split intelligently for two different questions
          const toneText = safeExtractText(toneData);
          const sentences = toneText.split('. ');
          if (sentences.length >= 2) {
            // First question: Tone and style (first part)
            const midPoint = Math.ceil(sentences.length / 2);
            const tonePart = sentences.slice(0, midPoint).join('. ') + (sentences.slice(0, midPoint).join('. ').endsWith('.') ? '' : '.');
            setToneOfVoice(tonePart.trim());
            
            // Second question: Signature words and phrases (second part)
            const signaturePart = sentences.slice(midPoint).join('. ') + (sentences.slice(midPoint).join('. ').endsWith('.') ? '' : '.');
            setToneOfVoice2(signaturePart.trim());
          } else {
            // If only one sentence, use it for tone and leave signature empty
            setToneOfVoice(toneText);
            setToneOfVoice2('');
          }
        }
      } else if (briefData["Brand Voice & Tone"]) {
        const toneData = briefData["Brand Voice & Tone"];
        
        // Handle nested object structure (new format)
        if (typeof toneData === 'object' && toneData !== null) {
          if (toneData.toneOfVoice) {
            setToneOfVoice(safeExtractText(toneData.toneOfVoice));
          }
          if (toneData.toneOfVoice2) {
            setToneOfVoice2(safeExtractText(toneData.toneOfVoice2));
          }
        } else {
          // Handle string format (old format) - split intelligently for two different questions
          const toneText = safeExtractText(toneData);
          const sentences = toneText.split('. ');
          if (sentences.length >= 2) {
            // First question: Tone and style (first part)
            const midPoint = Math.ceil(sentences.length / 2);
            const tonePart = sentences.slice(0, midPoint).join('. ') + (sentences.slice(0, midPoint).join('. ').endsWith('.') ? '' : '.');
            setToneOfVoice(tonePart.trim());
            
            // Second question: Signature words and phrases (second part)
            const signaturePart = sentences.slice(midPoint).join('. ') + (sentences.slice(midPoint).join('. ').endsWith('.') ? '' : '.');
            setToneOfVoice2(signaturePart.trim());
          } else {
            // If only one sentence, use it for tone and leave signature empty
            setToneOfVoice(toneText);
            setToneOfVoice2('');
          }
        }
      }

      // Step 7: USP
      if (briefData["USP(Unique SellingPropositions)"]) {
        const uspData = briefData["USP(Unique SellingPropositions)"];
        
        // Handle nested object structure (new format)
        if (typeof uspData === 'object' && uspData !== null) {
          if (uspData.uniqueSellingPoints) {
            setUniqueSellingPoints(safeExtractText(uspData.uniqueSellingPoints));
          }
          if (uspData.functionalBenefits) {
            setFunctionalBenefits(safeExtractText(uspData.functionalBenefits));
          }
        } else {
          // Handle string format (old format)
          setUniqueSellingPoints(safeExtractText(uspData));
        }
      }

      // Functional benefits & emotional angles (map from form field) - only if USP didn't already set it
      if (briefData["Think functional benefits, emotional angles, and cultural relevance."] && !briefData["USP(Unique SellingPropositions)"]?.functionalBenefits) {
        const fb = safeExtractText(briefData["Think functional benefits, emotional angles, and cultural relevance."]);
        if (fb) {
          setFunctionalBenefits(fb);
          // If emotionalBenefits not captured elsewhere, use same as fallback
          if (!emotionalBenefits) {
            setEmotionalBenefits(fb);
          }
        }
      }

      // Step 8: 5-Second Wow Factor
      if (briefData["5-Second Wow Factor"]) {
        const wowFactorData = briefData["5-Second Wow Factor"];
        
        // Handle nested object structure (new format)
        if (typeof wowFactorData === 'object' && wowFactorData !== null) {
          if (wowFactorData.painPoints) {
            setPainPoints(safeExtractText(wowFactorData.painPoints));
          }
        } else {
          // Handle string format (old format)
          setPainPoints(safeExtractText(wowFactorData));
        }
      }

      // Step 9: Key Features (With Context)
      if (briefData["Key Features (With Context)"]) {
        setKeyFeatures(safeExtractText(briefData["Key Features (With Context)"]));
      }

      // Step 10: Top 6 Selling Points
      if (briefData["Top 6 Selling Points (With Strategic Justification)"]) {
        const pts = briefData["Top 6 Selling Points (With Strategic Justification)"];
        
        // Handle nested object structure (new format)
        if (typeof pts === 'object' && pts !== null && pts.top6SellingPoints) {
          setEmotionalBenefits(safeExtractText(pts.top6SellingPoints));
        } else if (Array.isArray(pts)) {
          const formatted = pts
            .map((p: any) => {
              if (!p) return '';
              const point = safeExtractText(p.point || p.Point || p);
              const why = safeExtractText(p.why || p.Why || '');
              return point ? `${point}${why ? ' — ' + why : ''}` : '';
            })
            .filter(Boolean)
            .join('\n');
          // Reuse emotionalBenefits field for display block
          setEmotionalBenefits(formatted);
        } else {
          setEmotionalBenefits(safeExtractText(pts));
        }
      }

      // Step 11: Competitive Landscape
      if (briefData["Competitive Landscape"]) {
        const comp = briefData["Competitive Landscape"];
        if (typeof comp === 'object' && comp !== null) {
          const list = Array.isArray((comp as any).competitors) ? (comp as any).competitors : [];
          const c1 = list[0]?.name || list[0];
          const c2 = list[1]?.name || list[1];
          const c3 = list[2]?.name || list[2];
          if (c1) setCompetitor1(safeExtractText(c1));
          if (c2) setCompetitor2(safeExtractText(c2));
          if (c3) setCompetitor3(safeExtractText(c3));
          const diffs = Array.isArray((comp as any).amazon_differentiators) ? (comp as any).amazon_differentiators : [];
          if (diffs.length) setAmazonDifferentiators(diffs.map((d: any) => safeExtractText(d)).join(', '));
          const summaryPieces: string[] = [];
          if (c1 || c2 || c3) summaryPieces.push([c1, c2, c3].filter(Boolean).map((x) => safeExtractText(x as any)).join(', '));
          if (diffs.length) summaryPieces.push(`Amazon differentiators: ${diffs.map((d: any) => safeExtractText(d)).join(', ')}`);
          const pos = safeExtractText((comp as any).positioning_summary || '');
          if (pos) summaryPieces.push(pos);
          const final = summaryPieces.filter(Boolean).join(' | ');
          setCompetitorComparison(final || safeExtractText(comp));
        } else {
          setCompetitorComparison(safeExtractText(comp));
        }
      }

      // Step 12: Search & Keywords Strategy
      if (briefData["Search Keywords"]) {
        setSearchKeywords(safeExtractText(briefData["Search Keywords"]));
      } else if (briefData["Search & Keywords Strategy"]) {
        setSearchKeywords(safeExtractText(briefData["Search & Keywords Strategy"]));
      }

      // Step 13: Brand Story, Values & Purpose
      if (briefData["Brand Story"]) {
        const brandStoryData = briefData["Brand Story"];
        
        // Handle nested object structure (new format)
        if (typeof brandStoryData === 'object' && brandStoryData !== null) {
          if (brandStoryData.brandStory) {
            setBrandStory(safeExtractText(brandStoryData.brandStory));
          }
          if (brandStoryData.brandStory2) {
            setBrandStory2(safeExtractText(brandStoryData.brandStory2));
          }
        } else {
          // Handle string format (old format)
          setBrandStory(safeExtractText(brandStoryData));
        }
      }
      
      // Fallback: individual fields if Brand Story object didn't already set them
      if (briefData["Brand Story 2"] && !briefData["Brand Story"]?.brandStory2) {
        setBrandStory2(safeExtractText(briefData["Brand Story 2"]));
      }
      
      // Final fallback: combined field
      if (!brandStory && !brandStory2 && briefData["Brand Story, Values & Purpose"]) {
        const brandData = safeExtractText(briefData["Brand Story, Values & Purpose"]);
        const sentences = brandData.split('. ');
        if (sentences.length >= 2) {
          const mid = Math.ceil(sentences.length / 2);
          setBrandStory((sentences.slice(0, mid).join('. ') + (brandData.endsWith('.') ? '' : '.')).trim());
          setBrandStory2((sentences.slice(mid).join('. ') + (brandData.endsWith('.') ? '' : '.')).trim());
        } else {
          setBrandStory(brandData);
        }
      }

      // Step 14: Design Direction
      if (briefData["Design Direction"]) {
        const designData = briefData["Design Direction"];
        
        // Handle nested object structure (new format)
        if (typeof designData === 'object' && designData !== null) {
          if (designData.designDirection) {
            setDesignDirection(safeExtractText(designData.designDirection));
          }
          if (designData.designDirection2) {
            setDesignDirection2(safeExtractText(designData.designDirection2));
          }
        } else {
          // Handle string format (old format)
          const designText = safeExtractText(designData);
          if (!briefData["Design Direction 2"]) {
            // Split a single long description across two prompts
            const sentences = designText.split('. ');
            if (sentences.length >= 2) {
              const mid = Math.ceil(sentences.length / 2);
              setDesignDirection((sentences.slice(0, mid).join('. ') + (designText.endsWith('.') ? '' : '.')).trim());
              setDesignDirection2((sentences.slice(mid).join('. ') + (designText.endsWith('.') ? '' : '.')).trim());
            } else {
              setDesignDirection(designText);
            }
          } else {
            setDesignDirection(designText);
          }
        }
      }
      if (briefData["Design Direction 2"]) {
        setDesignDirection2(safeExtractText(briefData["Design Direction 2"]));
      }

      // Step 15: Final Notes
      if (briefData["Final Notes & Strategic Callouts"]) {
        setFinalNotes(safeExtractText(briefData["Final Notes & Strategic Callouts"]));
      }

      // Additional fields that might be missing - Only if Product Snapshot didn't already set it
      if (briefData["What problem does it solve?"] && !briefData["Product Snapshot"]?.problemSolved) {
        setProblemSolved(safeExtractText(briefData["What problem does it solve?"]));
      }
      
      // Only set functionalBenefits if USP didn't already set it
      if (briefData["Think functional benefits, emotional angles, and cultural relevance"] && !briefData["USP(Unique SellingPropositions)"]?.functionalBenefits) {
        setFunctionalBenefits(safeExtractText(briefData["Think functional benefits, emotional angles, and cultural relevance"]));
      }
      
      if (briefData["Top 6 Selling Points (With Strategic Justification)"]) {
        setEmotionalBenefits(safeExtractText(briefData["Top 6 Selling Points (With Strategic Justification)"]));
      }
      
      // Load individual Tone of Voice fields as final fallback
      if (briefData["Tone of Voice"] && !briefData["Tone of Voice"]?.toneOfVoice && !briefData["Brand Voice & Tone"]?.toneOfVoice) {
        setToneOfVoice(safeExtractText(briefData["Tone of Voice"]));
      }
      if (briefData["Tone of Voice 2"] && !briefData["Tone of Voice"]?.toneOfVoice2 && !briefData["Brand Voice & Tone"]?.toneOfVoice2) {
        setToneOfVoice2(safeExtractText(briefData["Tone of Voice 2"]));
      }
      
      // Target Customer Deep Dive fields are already loaded above in Step 4
      
      // Load Competitive Landscape fields
      if (briefData["Competitive Landscape"] && typeof briefData["Competitive Landscape"] === 'object') {
        const competitive = briefData["Competitive Landscape"];
        
        // Handle correct field names
        if (competitive.competitor1) {
          setCompetitor1(safeExtractText(competitive.competitor1));
        }
        if (competitive.competitor2) {
          setCompetitor2(safeExtractText(competitive.competitor2));
        }
        if (competitive.competitor3) {
          setCompetitor3(safeExtractText(competitive.competitor3));
        }
        if (competitive.competitorComparison) {
          setCompetitorComparison(safeExtractText(competitive.competitorComparison));
        }
        if (competitive.amazonDifferentiators) {
          setAmazonDifferentiators(safeExtractText(competitive.amazonDifferentiators));
        }
        
        // Handle old format fields as fallback
        if (competitive["Competitor 1"] && !competitive.competitor1) {
          setCompetitor1(safeExtractText(competitive["Competitor 1"]));
        }
        if (competitive["Competitor 2"] && !competitive.competitor2) {
          setCompetitor2(safeExtractText(competitive["Competitor 2"]));
        }
        if (competitive["Competitor 3"] && !competitive.competitor3) {
          setCompetitor3(safeExtractText(competitive["Competitor 3"]));
        }
        if (competitive["Describe how this product compares"] && !competitive.competitorComparison) {
          setCompetitorComparison(safeExtractText(competitive["Describe how this product compares"]));
        }
        if (competitive["Amazon Differentiators"] && !competitive.amazonDifferentiators) {
          setAmazonDifferentiators(safeExtractText(competitive["Amazon Differentiators"]));
        }
      }
      
      // Fallback: Try to load competitor names from other possible fields
      if (!competitor1 && !competitor2 && !competitor3) {
        // Try to extract from competitor comparison text
        const compText = competitorComparison || '';
        if (compText) {
          const words = compText.split(/[,\s]+/).filter(word => word.length > 2);
          if (words.length >= 1) setCompetitor1(words[0]);
          if (words.length >= 2) setCompetitor2(words[1]);
          if (words.length >= 3) setCompetitor3(words[2]);
        }
        
        // Try to extract from other possible fields
        if (briefData["Competitors"]) {
          const competitors = briefData["Competitors"];
          if (typeof competitors === 'string') {
            const compList = competitors.split(/[,\n]/).map(c => c.trim()).filter(Boolean);
            if (compList[0]) setCompetitor1(compList[0]);
            if (compList[1]) setCompetitor2(compList[1]);
            if (compList[2]) setCompetitor3(compList[2]);
          }
        }
      }
      
      // Age chart data should be loaded from the form data above
      // No fallback needed - use actual form data
    }
  }, [dashboardData]);

  const downloadProjectSummaryAsPDF = async () => {
    const doc = new jsPDF();
    // Helper: create a high-DPI pie chart image from data with percentage labels
    const createPieImage = (
      segments: Array<{ value: number; color: string }>,
      displaySizeMm = 55
    ): string => {
      // Convert mm to px at 96dpi (1mm ≈ 3.78px) and render at 2x for crispness
      const mmToPx = (mm: number) => Math.round(mm * 3.78);
      const sizePx = mmToPx(displaySizeMm) * 2; // 2x for HiDPI
      const canvas = document.createElement('canvas');
      canvas.width = sizePx; canvas.height = sizePx;
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      ctx.clearRect(0, 0, sizePx, sizePx);
      const total = segments.reduce((s, seg) => s + (isNaN(seg.value) ? 0 : seg.value), 0) || 1;
      let start = -Math.PI / 2;
      const cx = sizePx / 2, cy = sizePx / 2, r = (sizePx / 2) - Math.round(sizePx * 0.06);
      segments.forEach(seg => {
        const angle = (seg.value / total) * Math.PI * 2;
        // Slice
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, r, start, start + angle);
        ctx.closePath();
        ctx.fillStyle = seg.color;
        ctx.fill();
        // Label at mid-angle
        const mid = start + angle / 2;
        const lx = cx + Math.cos(mid) * (r * 0.7);
        const ly = cy + Math.sin(mid) * (r * 0.7);
        const pct = Math.round((seg.value / total) * 100);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `${Math.round(sizePx * 0.06)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (pct > 0) ctx.fillText(`${pct}%`, lx, ly);
        start += angle;
      });
      return canvas.toDataURL('image/png');
    };

    // Normalize text to avoid PDF wrapping glitches (zero-width spaces, NBSP, smart quotes)
    const normalizePdfText = (raw: string): string => {
      if (!raw) return '';
      return String(raw)
        .replace(/\u00A0/g, ' ')   // NBSP
        .replace(/\u200B/g, '')    // zero-width space
        .replace(/\u202F/g, ' ')   // narrow NBSP
        .replace(/\u3000/g, ' ')   // ideographic space
        .replace(/[""]/g, '"')     // smart double quotes
        .replace(/[']/g, "'")     // smart single quote
        .replace(/\s+/g, ' ')      // collapse whitespace
        // Do not break mid-word; keep extremely long tokens intact
        .trim();
    };

    // Helper: insert soft breaks inside very long tokens only (for specific dense sections)
    const insertSoftBreaks = (raw: string, run: number = 18): string => {
      if (!raw) return '';
      const re = new RegExp(`(\\S{${run}})(?=\\S)`, 'g');
      return String(raw).replace(re, '$1\u200B');
    };

    // Helper: convert inline dash lists (" - item") into newline-separated bullets
    const dashListToLines = (raw: string): string => {
      if (!raw) return '';
      // Replace occurrences of space-hyphen-space with newlines
      return String(raw).replace(/\s+-\s+/g, '\n').trim();
    };

    // Helper: measure content box required height without drawing
    const measureBoxHeight = (
      width: number,
      minHeight: number,
      text: string,
      title: string | null = null
    ): number => {
      const padding = 14; // must match createContentBox
      const innerWidth = width - padding * 2 - 6; // match createContentBox
      const contentFontSize = 10; // match createContentBox
      const titleFontSize = 12; // match createContentBox
      const contentLineHeight = 4.6; // match createContentBox
      const titleLineHeight = 5.2; // match createContentBox
      // simulate text wrapping
      doc.setFontSize(titleFontSize);
      const titleLines = title ? doc.splitTextToSize(title, innerWidth) : [] as any;
      doc.setFontSize(contentFontSize);
      const contentLines = doc.splitTextToSize(normalizePdfText(text), innerWidth);
      const contentHeight = contentLines.length * contentLineHeight;
      const titleHeight = (titleLines as string[]).length > 0 ? (titleLines as string[]).length * titleLineHeight + 2 : 0;
      return Math.max(minHeight, padding + titleHeight + contentHeight + padding);
    };
    let yPosition = 30;
    const headerHeight = 64; // compact header height
    const pageHeight = 280; // Adjusted for header only (no footer)
    let currentPage = 1;
    const totalPages = 15; // Total number of sections
    
         // Helper function to add new page if needed
     const addPageIfNeeded = (requiredSpace = 20) => {
       if (yPosition + requiredSpace > pageHeight) {
         doc.addPage();
         currentPage++;
         // Only first page has header; on following pages just reset y
         yPosition = 20;
       }
     };
    
         // Helper function to add header to each page
     const addHeader = (pageDoc: any) => {
       // Header background (fallback gradient color)
       pageDoc.setFillColor(179, 145, 232); // #B391E8
       pageDoc.rect(0, 0, 210, headerHeight, 'F');

       // Header image overlay (includes soft gradient)
       try {
         pageDoc.addImage(headerImage, 'PNG', 0, 0, 210, headerHeight);
       } catch (error) {
         // keep color fill
       }

       // Brand logo directly on header (no white box)
       try {
         const logoW = 46;
         const logoH = 46;
         const logoX = 10; // move left
         const logoY = Math.round((headerHeight - logoH) / 2); // vertically centered
         pageDoc.addImage(logoImage, 'PNG', logoX, logoY, logoW, logoH);

         // Title left-aligned and vertically centered with logo
         pageDoc.setFontSize(18);
         pageDoc.setFont('helvetica', 'bold');
         pageDoc.setTextColor(39, 34, 100); // #272264
         const titleX = logoX + logoW + 12;
         const titleY = logoY + Math.round(logoH / 2) + 5; // optical center
         pageDoc.text('Onboarding Report', titleX, titleY);
       } catch (error) {
         // skip if not available
       }

       // Page number pill (auto width/height based on text) aligned vertically with title
       const pageLabel = `PAGES ${currentPage}/${totalPages}`;
       const pillFontSize = 10;
       pageDoc.setFont('helvetica', 'normal');
       pageDoc.setFontSize(pillFontSize);
       const padX = 4; // mm (roomy to avoid clipping)
       const padY = 4; // mm
       const textWidthMm = pageDoc.getTextWidth(pageLabel); // in mm
       const textHeightMm = pillFontSize * 0.352778; // pt->mm
       const pillW = textWidthMm + padX * 2;
       const pillH = textHeightMm + padY * 2;
       const pillY = Math.round((headerHeight - pillH) / 2);
       const pillX = 210 - 20 - pillW; // ~20mm right margin
       // Shadows (approximate CSS box-shadow using semi-transparent fills if available)
       const shadowR = 25 * 0.264583; // 25px -> mm
       const setOpacity = (opacity: number) => {
         const anyDoc: any = pageDoc as any;
         if (anyDoc.setGState && anyDoc.GState) {
           anyDoc.setGState(new anyDoc.GState({ opacity }));
           return true;
         }
         return false;
       };
       const resetOpacity = () => {
         const anyDoc: any = pageDoc as any;
         if (anyDoc.setGState && anyDoc.GState) {
           anyDoc.setGState(new anyDoc.GState({ opacity: 1 }));
         }
       };
       // First shadow: 0 2px 4px rgba(54,61,85,0.15)
       if (setOpacity(0.15)) {
         pageDoc.setFillColor(54, 61, 85);
         pageDoc.roundedRect(pillX, pillY + (2 * 0.264583), pillW, pillH, shadowR, shadowR, 'F');
         resetOpacity();
       }
       // Second shadow: 0 1px 2px rgba(54,61,85,0.08)
       if (setOpacity(0.08)) {
         pageDoc.setFillColor(54, 61, 85);
         pageDoc.roundedRect(pillX, pillY + (1 * 0.264583), pillW, pillH, shadowR, shadowR, 'F');
         resetOpacity();
       }
       // White pill without border (no stroke)
       pageDoc.setFillColor(255, 255, 255);
       const pillRadius = 25 * 0.264583; // 25px in mm
       pageDoc.roundedRect(pillX, pillY, pillW, pillH, pillRadius, pillRadius, 'F');
       pageDoc.setFont('helvetica', 'normal');
       pageDoc.setFontSize(pillFontSize);
       pageDoc.setTextColor(80, 80, 90);
       const pillCenterX = pillX + pillW / 2;
       const baselineOffset = textHeightMm * 0.35; // approximate baseline correction
       const pillCenterY = pillY + pillH / 2 + baselineOffset;
       pageDoc.text(pageLabel, pillCenterX, pillCenterY, { align: 'center' });
     };
    
    
    
         // Helper function to create content box
      const createContentBox = (
        x: number,
        y: number,
        width: number,
        minHeight: number,
        bgColor: number[],
        borderColor: number[],
        text: string,
        title: string | null = null
      ) => {
        const leftPad = 8; // left padding
        const rightPad = 8; // right padding
        const paddingY = 14; // a bit more vertical padding

        // Readable, balanced metrics
        const innerWidth = Math.max(width - (leftPad + rightPad + 6), 30);
        const contentFontSize = 10;
        const titleFontSize = 12;
        const contentLineHeight = 4.6;
        const titleLineHeight = 5.2;

        // Measure title and content with proper font sizing
        doc.setFontSize(titleFontSize);
        const titleLines = title ? doc.splitTextToSize(title, innerWidth) : [] as string[];
        
        // Convert content into bullet lines and then wrap with proper font sizing
        doc.setFontSize(contentFontSize);
        const bulletLines = formatToBulletLines(normalizePdfText(text));
        const contentLines = bulletLines
          .map((ln) => doc.splitTextToSize(ln, innerWidth))
          .flat();
        
        const contentHeight = contentLines.length * contentLineHeight;
        const titleHeight = titleLines.length > 0 ? titleLines.length * titleLineHeight + 2 : 0;

        // Calculate required height based on content
        const calculatedHeight = paddingY + titleHeight + contentHeight + paddingY;
        let requiredHeight = Math.max(minHeight, calculatedHeight);

        // If this box would overflow the page, move to new page BEFORE drawing
        if (y + requiredHeight > pageHeight) {
          doc.addPage();
          currentPage++;
          y = 20; // top margin on new page
        }

        // Background and border
        doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        doc.setDrawColor(
          Array.isArray(borderColor) && borderColor.length === 3 ? borderColor[0] : 226,
          Array.isArray(borderColor) && borderColor.length === 3 ? borderColor[1] : 213,
          Array.isArray(borderColor) && borderColor.length === 3 ? borderColor[2] : 255
        );
        doc.setLineWidth(0.4);
        const r8px = 8 * 0.264583; // 8px in mm
        doc.roundedRect(x, y, width, requiredHeight, r8px, r8px, 'FD');

        // Title
        let contentStartY = y + paddingY;
        if (title) {
          doc.setFontSize(titleFontSize);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(39, 34, 100);
          if ((titleLines as string[]).length > 0) {
            doc.text(titleLines as any, x + leftPad, contentStartY);
            contentStartY += titleHeight; // space after wrapped title
          }
        }

        // Content (draw plain wrapped lines; no mid-word breaking)
        doc.setFontSize(contentFontSize);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(39, 34, 100);
        let drawn = 0;
        let currentY = contentStartY;
        while (drawn < contentLines.length) {
          const line = String(contentLines[drawn]);
          if (currentY > pageHeight - 15) {
            doc.addPage();
            currentPage++;
            currentY = 20;
          }
          doc.text(line, x + leftPad, currentY, { align: 'left' } as any);
          currentY += contentLineHeight;
          drawn++;
        }

        return y + requiredHeight + 6;
      };
      
      // Make bullets: split on newlines; prepend • and trim
      const formatToBulletLines = (raw: string): string[] => {
        if (!raw) return [''];
        const parts = raw.split(/\r?\n+/).map((s) => s.trim()).filter(Boolean);
        if (parts.length === 0) return [''];
        return parts.map((p) => (p.startsWith('•') ? p : `• ${p}`));
      };

      // Draw a line and bold the text inside double quotes using multiple text calls
      // removed unused helper
     
      // Helper function to create section header
      const createSectionHeader = (title: string, y: number) => {
        const baselineY = y + 8;
        const boxesLeftX = 20; // align with content boxes left edge
        const textGap = 3;     // small gap between bar and title

        // Very thin vertical bar aligned with boxes (reduced height)
        doc.setDrawColor(151, 110, 222);
        doc.setLineWidth(1);
        doc.line(boxesLeftX, baselineY - 3, boxesLeftX, baselineY + 3);

        // Heading aligned to boxes with small horizontal gap
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(129, 97, 191);
        doc.text(title, boxesLeftX + textGap, baselineY + 2);
        return baselineY + 10;
      };

      // Info card style used in Project Overview grid (legacy, kept for reference but unused)
      /*
      const createInfoCard = (
        x: number,
        y: number,
        w: number,
        minH: number,
        label: string,
        value: string,
      ): number => {
        const padding = 12;
        const innerW = w - padding * 2;
        // measure value
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const lines = doc.splitTextToSize(value, innerW);
        const lineHeight = 3.6;
        const textHeight = lines.length * lineHeight;
        const usedH = Math.max(minH, padding * 2 + 10 + textHeight);

        // card with 2px radius
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(226, 230, 240);
        doc.setLineWidth(0.4);
        const r2px = 2 * 0.264583; // 2px in mm
        doc.roundedRect(x, y, w, usedH, r2px, r2px, 'FD');

        // label (8px bold)
        doc.setTextColor(39, 34, 100);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(label + ':', x + padding, y + padding);

        // value (8px)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const isUrl = /^(https?:\/\/|www\.)/i.test(value);
        doc.setTextColor(isUrl ? 86 : 39, isUrl ? 77 : 34, isUrl ? 184 : 100);
        lines.forEach((ln: string, idx: number) => {
          const yLine = y + padding + 10 + idx * lineHeight;
          doc.text(ln, x + padding, yLine, { align: 'left' });
        });
        doc.setTextColor(39, 34, 100);
        return usedH;
      };
      */

      // New helpers for precise equal-height cards with sharper corners and tighter padding
      const measureInfoCardHeight = (
        w: number,
        minH: number,
        _label: string,
        value: string
      ): number => {
        const padding = 6; // tighter inside padding
        const innerW = w - padding * 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const lines = doc.splitTextToSize(value || '—', innerW);
        const lineHeight = 3.2;
        const textHeight = lines.length * lineHeight;
        // 8 (approx) for single label line spacing
        return Math.max(minH, padding * 2 + 8 + textHeight);
      };

      const drawInfoCard = (
        x: number,
        y: number,
        w: number,
        h: number,
        label: string,
        value: string
      ) => {
        const padding = 6;
        const innerW = w - padding * 2;
        // Card box styling with 8px radius
        // border: 0.6px solid #DDD; background: #F9F9F9;
        doc.setFillColor(249, 249, 249); // #F9F9F9
        doc.setDrawColor(221, 221, 221); // #DDD
        doc.setLineWidth(0.4);
        const r8px = 8 * 0.264583; // 8px in mm
        // Fill + stroke (0.6px)
        doc.roundedRect(x, y, w, h, r8px, r8px, 'FD');

        // Compute vertical centering for label + value block
        const lineHeight = 3.2; // value line height
        const labelHeight = 3.2; // treat label as one line height
        const labelGap = 4.0; // minimal gap between label and first value line
        const valueLines = doc.splitTextToSize(value || '—', innerW);
        const blockHeight = labelHeight + labelGap + (valueLines.length * lineHeight);
        const startY = y + Math.max(padding, (h - blockHeight) / 2);

        // Label (bold)
        doc.setTextColor(39, 34, 100);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(label + ':', x + padding, startY + labelHeight, { align: 'left' });

        // Value (wrapped, left aligned), centered block
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        for (let i = 0; i < valueLines.length; i++) {
          doc.text(String(valueLines[i]), x + padding, startY + labelHeight + labelGap + i * lineHeight, { align: 'left' });
        }
      };
    
    // Add header to first page
    addHeader(doc);
    yPosition = headerHeight + 22; // Start after header
    
     // Project Overview Section
     addPageIfNeeded(80);
     yPosition = createSectionHeader('Project Overview', yPosition);

     // Grid: row 1 has 3 equal columns; row 2 has 2 columns at 50% width each
     const marginX = 20;
     const fullW = 150;
     const gap = 6;

     // Row 1 (measure then draw equal height)
     const colW = (fullW - gap * 2) / 3;
     const r1Y = yPosition;
     const r1Heights = [
       measureInfoCardHeight(colW, 24, 'Project name', projectName || '—'),
       measureInfoCardHeight(colW, 24, 'Brand name', brandName || '—'),
       measureInfoCardHeight(colW, 24, 'Website', websiteLink || '—'),
     ];
     const r1MaxH = Math.max(...r1Heights);
     drawInfoCard(marginX, r1Y, colW, r1MaxH, 'Project name', projectName || '—');
     drawInfoCard(marginX + colW + gap, r1Y, colW, r1MaxH, 'Brand name', brandName || '—');
     drawInfoCard(marginX + (colW + gap) * 2, r1Y, colW, r1MaxH, 'Website', websiteLink || '—');

     // Row 2 (measure then draw equal height)
     const r2Y = r1Y + r1MaxH + 8;
     const halfW = (fullW - gap) / 2;
     const r2Heights = [
       measureInfoCardHeight(halfW, 24, 'Amazon listing (if available)', amazonListing || '—'),
       measureInfoCardHeight(halfW, 24, 'Instagram handle (if applicable)', instagram || '—'),
     ];
     const r2MaxH = Math.max(...r2Heights);
     drawInfoCard(marginX, r2Y, halfW, r2MaxH, 'Amazon listing (if available)', amazonListing || '—');
     drawInfoCard(marginX + halfW + gap, r2Y, halfW, r2MaxH, 'Instagram handle (if applicable)', instagram || '—');
     yPosition = r2Y + r2MaxH + 10;
    
     // Product Snapshot Section (start immediately after listing row with smaller pre-check)
    addPageIfNeeded(40);
    yPosition = createSectionHeader('PRODUCT SNAPSHOT', yPosition);
    
    // Product snapshot cards with correct data mapping
    yPosition = createContentBox(20, yPosition, 150, 32, [255, 247, 237] as any, [255, 214, 167] as any,
      productName || 'Product information not available', 'What exactly is the product?');
    yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255] as any, [190, 219, 255] as any,
      productDescription || 'Product description not available', 'What does it do and how does it work?');
    yPosition = createContentBox(20, yPosition, 150, 50, [240, 253, 244] as any, [185, 248, 207] as any,
      problemSolved || 'Problem solution not available', 'What problem does it solve?');
    yPosition = createContentBox(20, yPosition, 150, 32, [255, 247, 237] as any, [255, 214, 167] as any,
      targetAudience2 || 'Target audience not available', 'Who is it meant for?');
    
    // Current Listing Challenges Section
    addPageIfNeeded(80);
    yPosition = createSectionHeader('CURRENT LISTING CHALLENGES', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 32, [255, 247, 237], [255, 214, 167], 
      currentChallenges || 'Current challenges not available', 
      'What\'s broken or underwhelming about the current Amazon listing, brand positioning, or creative execution?');
    
    
    yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255], [190, 219, 255], 
      conversionLoss || 'Conversion loss information not available', 
      'Where are they losing conversions or attention?');
    
    
    // Target Customer Deep Dive Section
    addPageIfNeeded(70);
    yPosition = createSectionHeader('TARGET CUSTOMER DEEP DIVE', yPosition + 2);

    // Add charts row (Gender, Age)
    try {
      // add a small margin above cards
      yPosition += 6;
      // Card dimensions (mm)
      const cardW = 80; const cardH = 50; const gap = 10;
      const cardX1 = 20; const cardX2 = cardX1 + cardW + gap; const cardY = yPosition - 4;
      // Match other boxes radius (8px)
      const radius = 8 * 0.264583; // 8px in mm
      const titlePadTop = 5; // space from top border
      const sidePad = 4; // side padding for title
      // Gender card background
      doc.setFillColor(239, 246, 255); // #EFF6FF
      doc.setDrawColor(190, 219, 255); // #BEDBFF
      doc.setLineWidth(0.4);
      // Fill + stroke (0.6px)
      doc.roundedRect(cardX1, cardY, cardW, cardH, radius, radius, 'FD');
      // Age card background
      doc.setFillColor(239, 246, 255);
      doc.setDrawColor(190, 219, 255);
      doc.setLineWidth(0.4);
      // Fill + stroke (0.6px)
      doc.roundedRect(cardX2, cardY, cardW, cardH, radius, radius, 'FD');

      // Titles with margin away from top border
      doc.setFontSize(11); doc.setFont('helvetica','bold'); doc.setTextColor(39,34,100);
      doc.text('Gender', cardX1 + sidePad, cardY + titlePadTop);
      doc.text('Age', cardX2 + sidePad, cardY + titlePadTop);

      // Render charts strictly from form-derived state (no fallback inference)
      const imgW = 40; const imgH = 40;
      const norm = (arr: Array<{ value: number, color: string }>) => {
        const total = arr.reduce((s, x) => s + (isNaN(x.value) ? 0 : x.value), 0);
        if (total <= 0) return arr.map(x => ({ value: 0, color: x.color }));
        return arr.map(x => ({ value: Math.max(0, Math.min(100, (x.value / total) * 100)), color: x.color }));
      };
      const sumVals = (arr: Array<{ value: number }>) => arr.reduce((s, x) => s + (isNaN(x.value) ? 0 : x.value), 0);

      let gArr = genderChartData.map(g => ({ value: g.value, color: g.color || '#6B4EFF' }));
      let aArr = ageChartData.map(a => ({ value: a.value, color: a.color || '#8B5CF6' }));

      // If state hasn't populated yet, derive from demographics string captured from the form page
      if (sumVals(gArr) === 0) {
        // Try extracting directly from brief JSON (most reliable)
        try {
          const briefAny: any = (dashboardData as any)?.creative_brief?.brief_json;
          let b = briefAny;
          if (typeof briefAny === 'string') b = JSON.parse(briefAny);
          const tc = b?.['Target Customer Deep Dive'];
          // Prefer explicit Gender; if missing, fallback to Demographics text
          const gText = tc ? safeExtractText(tc.Gender || tc.gender || tc.Demographics || tc.demographics || '') : '';
          if (gText) {
            const srcLocal = gText.toLowerCase();
            const m = /(?:male|men)\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*(?:male|men)/.exec(srcLocal);
            const f = /(?:female|women)\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*(?:female|women)/.exec(srcLocal);
            const o = /(?:other|non-?binary|nb)\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*(?:other|non-?binary|nb)/.exec(srcLocal);
            const mv = m ? parseFloat(m[1] || m[2]) : NaN;
            const fv = f ? parseFloat(f[1] || f[2]) : NaN;
            const ov = o ? parseFloat(o[1] || o[2]) : NaN;
            // If only two values exist, infer the third
            const provided = [mv, fv, ov].filter(v => !isNaN(v));
            let maleV = isNaN(mv) ? 0 : mv;
            let femaleV = isNaN(fv) ? 0 : fv;
            let otherV = isNaN(ov) ? 0 : ov;
            if (provided.length === 2) {
              const missingIdx = [mv, fv, ov].findIndex(v => isNaN(v));
              const sumKnown = (isNaN(mv)?0:mv) + (isNaN(fv)?0:fv) + (isNaN(ov)?0:ov);
              const remain = Math.max(0, 100 - sumKnown);
              if (missingIdx === 0) maleV = remain; else if (missingIdx === 1) femaleV = remain; else otherV = remain;
            }
            const total = Math.max(1, mv + fv + ov);
            gArr = [
              { value: (maleV / total) * 100, color: '#6B4EFF' },
              { value: (femaleV / total) * 100, color: '#FF6B9D' },
              { value: (otherV / total) * 100, color: '#4ECDC4' }
            ];
          }
        } catch {}
      }
      if (sumVals(gArr) === 0) {
        const src = [targetAudience2, demographics].filter(Boolean).join(' | ').toLowerCase();
        const m = /male\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*male/.exec(src);
        const f = /female\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*female/.exec(src);
        const o = /(?:other|non-?binary|nb)\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*(?:other|non-?binary|nb)/.exec(src);
        const mv = m ? parseFloat(m[1] || m[2]) : 0;
        const fv = f ? parseFloat(f[1] || f[2]) : 0;
        const ov = o ? parseFloat(o[1] || o[2]) : 0;
        const total = Math.max(1, mv + fv + ov);
        gArr = [
          { value: (mv / total) * 100, color: '#6B4EFF' },
          { value: (fv / total) * 100, color: '#FF6B9D' },
          { value: (ov / total) * 100, color: '#4ECDC4' }
        ];
      }

      if (sumVals(aArr) === 0) {
        // Try extracting directly from brief JSON
        try {
          const briefAny: any = (dashboardData as any)?.creative_brief?.brief_json;
          let b = briefAny;
          if (typeof briefAny === 'string') b = JSON.parse(briefAny);
          const tc = b?.['Target Customer Deep Dive'];
          // Prefer explicit Age; if missing, fallback to Demographics text
          const aText = tc ? safeExtractText(tc.Age || tc.age || tc.Demographics || tc.demographics || '') : '';
          if (aText) {
            const p = /primary\s*([0-9]+)[-–]([0-9]+)/i.exec(aText);
            const s = /secondary\s*([0-9]+)[-–]([0-9]+)/i.exec(aText);
            const core = /([0-9]+)[-–]([0-9]+)\s*(?:core|{core}|\(core\))/i.exec(aText);
            const sec = /([0-9]+)[-–]([0-9]+)\s*(?:secondary|{secondary}|\(secondary\))/i.exec(aText);
            if ((p && s) || (core && sec)) {
              // Use labels from the text if available
              // Labels are not required in image output; only order matters
              aArr = [ { value: 70, color: '#3B82F6' }, { value: 30, color: '#10B981' } ];
            }
          }
        } catch {}
      }
      if (sumVals(aArr) === 0) {
        const src = [targetAudience2, demographics].filter(Boolean).join(' | ');
        const p = /primary\s*([0-9]+)[-–]([0-9]+)/i.exec(src);
        const s = /secondary\s*([0-9]+)[-–]([0-9]+)/i.exec(src);
        const core = /([0-9]+)[-–]([0-9]+)\s*(?:core|{core}|\(core\))/i.exec(src);
        const sec = /([0-9]+)[-–]([0-9]+)\s*(?:secondary|{secondary}|\(secondary\))/i.exec(src);
        if ((p && s) || (core && sec)) {
          aArr = [ { value: 70, color: '#3B82F6' }, { value: 30, color: '#10B981' } ];
        }
      }

      const genderImg = sumVals(gArr) > 0 ? createPieImage(norm(gArr), 42) : '';
      const ageImg = sumVals(aArr) > 0 ? createPieImage(norm(aArr), 42) : '';
      const imgY = cardY + titlePadTop + 2; // below title
      const imgX1 = cardX1 + (cardW - imgW) / 2;
      const imgX2 = cardX2 + (cardW - imgW) / 2;
      if (genderImg) doc.addImage(genderImg, 'PNG', imgX1, imgY, imgW, imgH);
      if (ageImg) doc.addImage(ageImg, 'PNG', imgX2, imgY, imgW, imgH);
      yPosition += cardH + 8;
    } catch {}
    
    // Add demographic boxes: compute individual heights and align to tallest to prevent overlap
    const demoBoxes = [
      { label: 'Location', value: location || '—', color: [250, 245, 255], border: [233, 212, 255] },
      { label: 'Income', value: income || '—', color: [239, 246, 255], border: [190, 219, 255] },
      { label: 'Profession', value: profession || '—', color: [240, 253, 244], border: [185, 248, 207] }
    ];
    const demoRowY = yPosition;
    const demoW = 60; // widened for readability
    const demoGap = 4; // slight gap
    const h1m = measureBoxHeight(demoW, 24, demoBoxes[0].value, demoBoxes[0].label);
    const h2m = measureBoxHeight(demoW, 24, demoBoxes[1].value, demoBoxes[1].label);
    const h3m = measureBoxHeight(demoW, 24, demoBoxes[2].value, demoBoxes[2].label);
    const demoMaxH = Math.max(h1m, h2m, h3m);
    if (demoRowY + demoMaxH > pageHeight) {
      doc.addPage();
      currentPage++;
      yPosition = 20;
    }
    createContentBox(20, yPosition, demoW, demoMaxH, demoBoxes[0].color as any, demoBoxes[0].border as any, demoBoxes[0].value, demoBoxes[0].label);
    createContentBox(20 + (demoW + demoGap), yPosition, demoW, demoMaxH, demoBoxes[1].color as any, demoBoxes[1].border as any, demoBoxes[1].value, demoBoxes[1].label);
    createContentBox(20 + (demoW + demoGap) * 2, yPosition, demoW, demoMaxH, demoBoxes[2].color as any, demoBoxes[2].border as any, demoBoxes[2].value, demoBoxes[2].label);
    yPosition = yPosition + demoMaxH + 10;
    
    // Add Gender and Age fields (these are separate from the pie charts)
    yPosition = createContentBox(20, yPosition, 150, 32, [255, 247, 237], [255, 214, 167], 
      gender || 'Gender information not available', 'Gender');
    yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255], [190, 219, 255], 
      age || 'Age information not available', 'Age');
    yPosition = createContentBox(20, yPosition, 150, 32, [250, 245, 255], [233, 212, 255], 
      lifeStage || 'Life stage information not available', 
      'Life Stage or Identity (e.g., new moms, eco-conscious Gen Z, busy professionals)');
    
    if (demographics) {
      yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255], [190, 219, 255], demographics, 'Demographics');
    }
    if (psychographics) {
      yPosition = createContentBox(20, yPosition, 150, 32, [240, 253, 244], [185, 248, 207], psychographics, 'Psychographics');
    }

    // Shopping behavior (was missing in PDF)
    if (shoppingBehavior) {
      yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255], [190, 219, 255], 
        shoppingBehavior, 
        'How do they shop on Amazon?');
    }
    
    
    yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255], [190, 219, 255], 
      painPoints || 'Pain points information not available', 
      'Pain points, desires, motivations');
    
    
    // 5-Second Wow Factor Section
    addPageIfNeeded(60);
    yPosition = createSectionHeader('5-SECOND WOW FACTOR', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 32, [250, 245, 255], [233, 212, 255], 
      painPoints || 'Wow factor information not available', 
      'What would stop customers in their tracks?');
    
    
    // Barriers to Purchase Section
    addPageIfNeeded(60);
    yPosition = createSectionHeader('BARRIERS TO PURCHASE', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 32, [250, 245, 255], [233, 212, 255], 
      conversionLoss || 'Barriers to purchase not available', 
      'list the common doubts, hesitations, or faq-style friction points that stop people from buying – even if they like the product.');
    
    
    // Brand Voice & Tone Section
    addPageIfNeeded(80);
    yPosition = createSectionHeader('BRAND VOICE & TONE', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 32, [250, 245, 255], [233, 212, 255], 
      toneOfVoice || 'Tone of voice not available', 
      'Describe the tone and copywriting style the brand uses or should use (e.g., bold, sassy, informative, premium, conversational).');
    
    
    yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255], [190, 219, 255], 
      toneOfVoice2 || 'Signature words not available', 
      'Include any signature words, phrases, or linguistic quirks.');
    
    
    // USPs Section
    addPageIfNeeded(80);
    yPosition = createSectionHeader('USPs (UNIQUE SELLING PROPOSITIONS)', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 32, [255, 247, 237], [255, 214, 167], 
      uniqueSellingPoints || 'USP information not available', 
      'What makes this product meaningfully different from other options in the category?');
    
    
    yPosition = createContentBox(20, yPosition, 150, 50, [240, 253, 244], [185, 248, 207], 
      (functionalBenefits || emotionalBenefits || 'Functional benefits not available'), 
      'Think functional benefits, emotional angles, and cultural relevance.');
    
    
    // Key Features Section
    addPageIfNeeded(60);
    yPosition = createSectionHeader('KEY FEATURES (WITH CONTEXT)', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 40, [250, 245, 255], [233, 212, 255], 
      keyFeatures || 'Key features not available', 
      'list 4-6 major features. but go beyond just the bullet points explain: why does this matter to the buyer? how does it connect to their lifestyle or values?');
    
    
    // Top 6 Selling Points Section (start immediately after Key Features)
    addPageIfNeeded(20);
    yPosition = createSectionHeader('TOP 6 SELLING POINTS (WITH STRATEGIC JUSTIFICATION)', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 60, [239, 246, 255], [190, 219, 255], 
      emotionalBenefits || 'Selling points not available', 
      'For each of the client\'s selected selling points: state the point. explain *why* it\'s strategically powerful for this product and customer.');
    
    
    // Competitive Landscape Section
    addPageIfNeeded(100);
    yPosition = createSectionHeader('COMPETITIVE LANDSCAPE', yPosition);
    
    // Draw competitor list card like the design (rows inside a colored card)
    const drawCompetitorListCard = (
      x: number,
      y: number,
      width: number,
      names: string[]
    ) => {
      const padding = 8; // mm around card content
      const gap = 3; // mm between competitor rows
      const rowPadX = 3; // text left padding inside row pill
      const rowPadY = 3; // vertical padding inside row pill
      const innerX = x + 3; // card inner start for rows
      const innerW = width - 6; // card inner width for rows
      const rows = names.filter(Boolean);
      const innerRows = rows.length > 0 ? rows : ['Competitor 1', 'Competitor 2', 'Competitor 3'];
      // Measure each row height based on wrapped lines
      doc.setFont('helvetica','bold');
      doc.setFontSize(10);
      const lineHeight = 4; // mm per line
      const rowHeights = innerRows.map((nm) => {
        const lines = doc.splitTextToSize(nm, innerW - rowPadX * 2);
        const textH = lines.length * lineHeight;
        return rowPadY * 2 + textH;
      });
      const titleHeight = 6; // mm
      const contentHeight = rowHeights.reduce((a,b)=>a+b,0) + gap * (rowHeights.length - 1);
      const height = padding * 2 + titleHeight + contentHeight;

      // Card background (orange theme)
      doc.setFillColor(255, 247, 237); // #FFF7ED
      doc.setDrawColor(255, 214, 167); // #FFD6A7
      doc.setLineWidth(0.6);
      const r8 = 8 * 0.264583;
      // Fill + stroke (0.6px)
      doc.roundedRect(x, y, width, height, r8, r8, 'FD');

      // Title inside card
      doc.setTextColor(116, 116, 116);
      doc.text('Describe how this product compares', x + 3, y + 5);

      // Draw rows, wrapping text within each pill
      let ry = y + padding + titleHeight;
      doc.setTextColor(39,34,100);
      innerRows.forEach((nm, idx) => {
        const rowH = rowHeights[idx];
        // Row pill background + 0.6px border
        doc.setFillColor(255, 234, 213); // light orange row
        doc.setLineWidth(0.4);
        const rr = 2 * 0.264583;
        doc.roundedRect(innerX, ry, innerW, rowH, rr, rr, 'FD');
        // Wrapped text lines
        const lines = doc.splitTextToSize(nm, innerW - rowPadX * 2);
        for (let i = 0; i < lines.length; i++) {
          const ty = ry + rowPadY + (i + 1) * lineHeight - 1; // baseline adjust
          doc.text(String(lines[i]), innerX + rowPadX, ty);
        }
        ry += rowH + gap;
      });
      return y + height + 6;
    };

    yPosition = drawCompetitorListCard(20, yPosition, 150, [competitor1, competitor2, competitor3]);
    
    
    yPosition = createContentBox(20, yPosition, 180, 40, [239, 246, 255], [190, 219, 255], 
      insertSoftBreaks(competitorComparison || 'Competitor comparison not available'), 
      'Describe how this product compares');
    
    
    yPosition = createContentBox(20, yPosition, 180, 40, [255, 247, 237], [255, 214, 167], 
      dashListToLines(insertSoftBreaks(amazonDifferentiators || 'Amazon differentiators not available', 14)), 
      'Mention any amazon-specific differentiators (e.g. bundle, shipping time, design)');
    
    
    // Assets Section in PDF (show immediately after Competitive Landscape if space allows)
    addPageIfNeeded(10);
    yPosition = createSectionHeader('ASSETS', yPosition);
    // List assets as content boxes
    yPosition = createContentBox(20, yPosition, 150, 24, [243, 229, 245], [226, 213, 255], hasAssets ? 'Yes' : 'No', 'Has assets');
    if (noAssetsReason && !hasAssets) {
      yPosition = createContentBox(20, yPosition, 150, 24, [239, 246, 255], [190, 219, 255], noAssetsReason, 'Reason for no assets');
    }
    if (whiteBackgroundLink) {
    yPosition = createContentBox(20, yPosition, 150, 24, [255, 247, 237], [255, 214, 167], whiteBackgroundLink, 'White background images');
    }
    if (oldImagesLink) {
      yPosition = createContentBox(20, yPosition, 150, 24, [239, 246, 255], [190, 219, 255], oldImagesLink, 'Old images');
    }
    if (lifestyleImageLink) {
      yPosition = createContentBox(20, yPosition, 150, 24, [235, 255, 235], [226, 213, 255], lifestyleImageLink, 'Lifestyle images');
    }
    if (userGeneratedContentLink) {
      yPosition = createContentBox(20, yPosition, 150, 24, [243, 229, 245], [226, 213, 255], userGeneratedContentLink, 'User generated content (UGC)');
    }
    if (videoContentLink) {
    yPosition = createContentBox(20, yPosition, 150, 24, [255, 247, 237], [255, 214, 167], videoContentLink, 'Video content');
    }

    // Search & Keywords Strategy Section
    addPageIfNeeded(60);
    yPosition = createSectionHeader('SEARCH & KEYWORDS STRATEGY', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 32, [250, 245, 255], [233, 212, 255], 
      searchKeywords || 'Search keywords not available', 
      'Suggest relevant search terms and keywords');
    
    
    // Brand Story Section
    addPageIfNeeded(80);
    yPosition = createSectionHeader('BRAND STORY, VALUES & PURPOSE', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 50, [250, 245, 255], [233, 212, 255], 
      brandStory || 'Brand story not available', 
      'Brand origin story:');
    
    
    yPosition = createContentBox(20, yPosition, 150, 50, [239, 246, 255], [190, 219, 255], 
      brandStory2 || 'Brand values not available', 
      'Core values and emotional drivers:');
    
    
    // Design Direction Section
    addPageIfNeeded(80);
    yPosition = createSectionHeader('DESIGN DIRECTION', yPosition);
    
    yPosition = createContentBox(20, yPosition, 150, 32, [250, 245, 255], [233, 212, 255], 
      designDirection || 'Design direction not available', 
      'Give a short but meaningful brand origin story or founder story.');
    
    
    yPosition = createContentBox(20, yPosition, 150, 32, [239, 246, 255], [190, 219, 255], 
      designDirection2 || 'Visual themes not available', 
      'Suggest how the visuals, layout, or color themes should feel (e.g., clean/minimal, bold/graphic, warm/natural).');
    
    
    // Final Notes Section
    addPageIfNeeded(60);
     yPosition = createSectionHeader('FINAL NOTES & STRATEGIC CALLOUTS', yPosition);
    
         yPosition = createContentBox(20, yPosition, 180, 50, [255, 247, 237], [255, 214, 167], 
       dashListToLines(insertSoftBreaks(finalNotes || 'Final notes not available', 14)), 
       'Final notes & strategic callouts');
     
     
     // Footer only on the last page
     try {
       const total = doc.getNumberOfPages();
       const pageW = doc.internal.pageSize.getWidth();
       const pageH = doc.internal.pageSize.getHeight();
       // Go to last page
       doc.setPage(total);
       // Draw footer background image across the bottom
       const footerHeight = 30; // mm
       doc.addImage(footerImage as any, 'PNG', 0, pageH - footerHeight, pageW, footerHeight);
       // Left-center text block
       const textX = 14; // mm from left
       // Vertically center three lines within footer
       const lineGap = 5; // mm between lines
       const textBlockHeight = lineGap * 2; // three lines -> two gaps
       const baseY = pageH - footerHeight + (footerHeight - textBlockHeight) / 2; // first line
       doc.setFont('helvetica','bold');
       doc.setFontSize(10);
       doc.setTextColor(39,34,100);
       doc.text('WorkinX Digital', textX, baseY);
       doc.setFont('helvetica','normal');
       doc.setFontSize(9);
       doc.text('https://www.workinxdigital.com/', textX, baseY + lineGap);
       doc.text('contact@xyz.com / (684) 879 - 0102', textX, baseY + lineGap * 2);
     } catch {}

     // Save the PDF
     doc.save('onboarding-report.pdf');
  };

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center py-12 px-2" style={{ background: 'linear-gradient(248deg, #B391E8 6.01%, #FDFDFF 98.77%)' }}>
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_0_rgba(107,78,255,0.10)] p-10">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6B4EFF]"></div>
            <span className="ml-3 text-[#272264] font-semibold">Loading project data...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center py-12 px-2" style={{ background: 'linear-gradient(248deg, #B391E8 6.01%, #FDFDFF 98.77%)' }}>
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_0_rgba(107,78,255,0.10)] p-10">
          <div className="text-center">
            <h2 className="text-xl font-bold text-[#272264] mb-4">Error Loading Project Data</h2>
            <p className="text-[#272264] mb-4">Unable to load the project summary. Please try again later.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-6 py-3 text-white font-semibold transition whitespace-nowrap"
              style={{
                borderRadius: '12px',
                background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center py-12 px-2" style={{ background: 'linear-gradient(248deg, #B391E8 6.01%, #FDFDFF 98.77%)' }}>
      <div className="w-full" style={{ maxWidth: '1150px' }}>
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_0_rgba(107,78,255,0.10)] p-10">
          {/* Success Banner */}
          <div className="mb-8 flex flex-col items-center justify-center" style={{
            borderRadius: '12px',
            background: 'linear-gradient(87deg, #21C45E 0.15%, #059768 107.96%)',
            height: '342px',
            padding: '48px'
          }}>
            <div className="w-28 h-28 bg-white bg-opacity-15 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="110" height="110" viewBox="0 0 110 111" fill="none">
                <path d="M83.5327 58.7123C81.2366 70.1928 72.5803 81.0028 60.434 83.4183C54.51 84.5981 48.3648 83.8788 42.8734 81.3628C37.382 78.8469 32.8244 74.6626 29.8494 69.4056C26.8745 64.1487 25.6339 58.0873 26.3043 52.0843C26.9747 46.0813 29.522 40.4428 33.5834 35.9717C41.9136 26.7965 55.9795 24.2708 67.46 28.863" stroke="white" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M44.4961 54.1198L55.9766 65.6003L83.5298 35.751" stroke="white" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="55" cy="55.5" r="55" fill="white" fill-opacity="0.15"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Submission Successful!</h1>
            <p className="text-white/90">Thank you for completing project onboarding</p>
          </div>

          {/* Project Status */}
          <div className="mb-8 text-center">
            <div className="flex justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="40" fill="#614EBE"/>
                <path d="M17.0762 39.167C27.9495 39.167 30.8106 52.9014 30.8106 52.9014C30.8106 52.9014 33.6717 39.167 44.545 39.167C33.6717 39.167 30.8106 25.4326 30.8106 25.4326C30.8106 25.4326 27.9495 39.167 17.0762 39.167Z" fill="white"/>
                <path d="M38.7617 52.9036C45.4916 54.3588 45.9903 60.8551 45.9903 60.8551C45.9903 60.8551 46.4891 54.3588 53.219 52.9036C46.4891 51.4485 45.9903 44.9521 45.9903 44.9521C45.9903 44.9521 45.4916 51.4485 38.7617 52.9036Z" fill="white"/>
                <path d="M40.207 29.0498C42.6452 29.5198 44.8704 30.754 46.56 32.5736C48.2496 34.3932 49.3159 36.7036 49.6043 39.1698C49.8926 36.7036 50.9589 34.3932 52.6485 32.5736C54.3381 30.754 56.5633 29.5198 59.0015 29.0498C56.5633 28.5798 54.3381 27.3455 52.6485 25.5259C50.9589 23.7064 49.8926 21.396 49.6043 18.9297C49.3159 21.396 48.2496 23.7064 46.56 25.5259C44.8704 27.3455 42.6452 28.5798 40.207 29.0498Z" fill="white"/>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-[#272264] mb-4">Your Project Is Now In Progress</h2>
            <p className="text-[#272264] text-lg leading-relaxed">
              We've received your complete project submission and our team is already working on<br></br> creating your optimized Amazon content. You'll receive updates throughout the process.
            </p>
          </div>

          {/* Project Summary */}
          <div className="mb-8">
            <div 
              className="flex items-center justify-between p-4 border border-[#E2D5FF] rounded-lg cursor-pointer hover:bg-[#F4F3FF] transition"
              onClick={() => setShowProjectSummary(!showProjectSummary)}
            >
              <span className="text-[#272264] font-semibold">View Project Summary</span>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: showProjectSummary ? 'rotate(150deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
              >
                <path d="M6 9L12 15L18 9" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            {/* Project Summary Content */}
            {showProjectSummary && (
              <div className="mt-4 border border-[#E2D5FF] rounded-lg p-6 bg-[#F4F3FF]">
                <div className="space-y-6">
                  {/* Project Overview */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Project Overview</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-[#E2D5FF]">
                        <span className="font-semibold text-[#272264]">Project Name:</span>
                        <span className="text-[#272264]">{projectName || 'Not specified'}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-[#E2D5FF]">
                        <span className="font-semibold text-[#272264]">Brand Name:</span>
                        <span className="text-[#272264]">{brandName || 'Not specified'}</span>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                        <div className="flex flex-col space-y-1">
                          <span className="font-semibold text-[#272264]">Website:</span>
                          <span className="text-[#272264] break-all">
                            {websiteLink ? (
                              <a 
                                href={websiteLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#564DB8] hover:underline"
                              >
                                {websiteLink}
                              </a>
                            ) : (
                              'Not specified'
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                        <div className="flex flex-col space-y-1">
                          <span className="font-semibold text-[#272264]">Amazon Listing:</span>
                          <span className="text-[#272264] break-all">
                            {amazonListing ? (
                              <a 
                                href={amazonListing} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#564DB8] hover:underline"
                              >
                                {amazonListing}
                              </a>
                            ) : (
                              'Not specified'
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                        <div className="flex flex-col space-y-1">
                          <span className="font-semibold text-[#272264]">Instagram (if applicable):</span>
                          <span className="text-[#272264] break-all">
                            {instagram ? (
                              <a 
                                href={instagram} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#564DB8] hover:underline"
                              >
                                {instagram}
                              </a>
                            ) : (
                              'Not specified'
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Product Snapshot */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Product Snapshot</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">What exactly is the product?</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{productName || 'Product information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">What does it do and how does it work?</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{productDescription || 'Product description not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">What problem does it solve?</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{problemSolved || 'Problem solution not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Who it is meant for?</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{targetAudience2 || 'Target audience not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assets Provided */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Assets</h3>
                    <div className="space-y-2">
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                        <div className="flex flex-col space-y-1">
                          <span className="font-semibold text-[#272264]">Has assets:</span>
                          <span className="text-[#272264]">{hasAssets ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                      {noAssetsReason && !hasAssets && (
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                          <div className="flex flex-col space-y-1">
                            <span className="font-semibold text-[#272264]">Reason for no assets:</span>
                            <span className="text-[#272264]">{noAssetsReason}</span>
                          </div>
                        </div>
                      )}
                      {whiteBackgroundLink && (
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                          <div className="flex flex-col space-y-1">
                            <span className="font-semibold text-[#272264]">White background images:</span>
                            <a className="text-[#564DB8] hover:underline break-all" href={whiteBackgroundLink} target="_blank" rel="noreferrer">{whiteBackgroundLink}</a>
                          </div>
                        </div>
                      )}
                      {oldImagesLink && (
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                          <div className="flex flex-col space-y-1">
                            <span className="font-semibold text-[#272264]">Old images:</span>
                            <a className="text-[#564DB8] hover:underline break-all" href={oldImagesLink} target="_blank" rel="noreferrer">{oldImagesLink}</a>
                          </div>
                        </div>
                      )}
                      {lifestyleImageLink && (
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                          <div className="flex flex-col space-y-1">
                            <span className="font-semibold text-[#272264]">Lifestyle images:</span>
                            <a className="text-[#564DB8] hover:underline break-all" href={lifestyleImageLink} target="_blank" rel="noreferrer">{lifestyleImageLink}</a>
                          </div>
                        </div>
                      )}
                      {userGeneratedContentLink && (
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                          <div className="flex flex-col space-y-1">
                            <span className="font-semibold text-[#272264]">UGC:</span>
                            <a className="text-[#564DB8] hover:underline break-all" href={userGeneratedContentLink} target="_blank" rel="noreferrer">{userGeneratedContentLink}</a>
                          </div>
                        </div>
                      )}
                      {videoContentLink && (
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF]">
                          <div className="flex flex-col space-y-1">
                            <span className="font-semibold text-[#272264]">Video content:</span>
                            <a className="text-[#564DB8] hover:underline break-all" href={videoContentLink} target="_blank" rel="noreferrer">{videoContentLink}</a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Current Listing Challenges */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Current Listing Challenges</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">What's broken or underwhelming about the current Amazon listing, brand positioning, or creative execution?</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{currentChallenges || 'Current challenges not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Where are they losing conversions or attention?</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{conversionLoss || 'Conversion loss information not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Target Customer Deep Dive */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Target Customer Deep Dive</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Gender:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{gender || 'Gender information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Age:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{age || 'Age information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Location:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{location || 'Location information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Income:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{income || 'Income information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Profession:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{profession || 'Profession information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Life Stage or Identity (e.g., new moms, eco-conscious Gen Z, busy professionals):</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{lifeStage || 'Life stage information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Demographics (Age, Gender, Income, Location, Education, etc.):</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{demographics || 'Demographics information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Psychographics:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{psychographics || 'Psychographics information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">How do they shop on Amazon?:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{shoppingBehavior || 'Shopping behavior information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Pain Points, Desires, Motivations:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{painPoints || 'Pain points information not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Barriers to Purchase */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Barriers to Purchase</h3>
                    <div>
                      <p className="font-semibold text-[#272264] mb-2">Common doubts or hesitations:</p>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                        <p className="text-[#272264]">{conversionLoss || 'Barriers to purchase not available'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Brand Voice & Tone */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Brand Voice & Tone</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Describe the tone and copywriting style the brand uses or should use (e.g., bold, sassy, informative, premium, conversational).</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{toneOfVoice || 'Tone of voice not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Include any signature words, phrases, or linguistic quirks.</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{toneOfVoice2 || 'Signature words not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* USPs */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">USPs (Unique Selling Propositions)</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">What makes this product meaningfully different from other options in the category?</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{uniqueSellingPoints || 'USP information not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Functional benefits:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{functionalBenefits || 'Functional benefits not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Emotional benefits:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{emotionalBenefits || 'Emotional benefits not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 5-Second Wow Factor */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">5-Second Wow Factor</h3>
                    <div>
                      <p className="font-semibold text-[#272264] mb-2">What would stop customers in their tracks?</p>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                        <p className="text-[#272264]">{painPoints || 'Wow factor information not available'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Key Features */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Key Features (With Context)</h3>
                    <div>
                      <p className="font-semibold text-[#272264] mb-2">Major features and why they matter:</p>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[80px]">
                        <p className="text-[#272264] whitespace-pre-line">
                          {keyFeatures || 'Key features not available'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Top 6 Selling Points */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Top 6 Selling Points (With Strategic Justification)</h3>
                    <div>
                      <p className="font-semibold text-[#272264] mb-2">Strategic selling points:</p>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[80px]">
                        <p className="text-[#272264] whitespace-pre-line">
                          {emotionalBenefits || 'Selling points not available'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Competitive Landscape */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Competitive Landscape</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="p-2 bg-white rounded-lg border border-[#E2D5FF] text-center">
                          <span className="font-semibold text-[#272264]">Competitor #1:</span>
                          <p className="text-[#272264]">{competitor1 || 'Not specified'}</p>
                        </div>
                        <div className="p-2 bg-white rounded-lg border border-[#E2D5FF] text-center">
                          <span className="font-semibold text-[#272264]">Competitor #2:</span>
                          <p className="text-[#272264]">{competitor2 || 'Not specified'}</p>
                        </div>
                        <div className="p-2 bg-white rounded-lg border border-[#E2D5FF] text-center">
                          <span className="font-semibold text-[#272264]">Competitor #3:</span>
                          <p className="text-[#272264]">{competitor3 || 'Not specified'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">How this product compares:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{competitorComparison || 'Competitor comparison not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Amazon-specific differentiators:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{amazonDifferentiators || 'Amazon differentiators not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Search & Keywords Strategy */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Search & Keywords Strategy</h3>
                    <div>
                      <p className="font-semibold text-[#272264] mb-2">Relevant search terms and keywords:</p>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                        <p className="text-[#272264]">{searchKeywords || 'Search keywords not available'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Brand Story */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Brand Story, Values & Purpose</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Brand origin story:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{brandStory || 'Brand story not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Core values and emotional drivers:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{brandStory2 || 'Brand values not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Design Direction */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Design Direction</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Aesthetic preferences:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{designDirection || 'Design direction not available'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[#272264] mb-2">Visual and color themes:</p>
                        <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                          <p className="text-[#272264]">{designDirection2 || 'Visual themes not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Final Notes */}
                  <div>
                    <h3 className="text-lg font-bold text-[#272264] mb-3">Final Notes & Strategic Callouts</h3>
                    <div>
                      <p className="font-semibold text-[#272264] mb-2">Extra insights for the creative team:</p>
                      <div className="p-3 bg-white rounded-lg border border-[#E2D5FF] min-h-[60px]">
                        <p className="text-[#272264]">{finalNotes || 'Final notes not available'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-center mt-4">
              <button 
                className="px-6 py-3 text-white font-semibold transition whitespace-nowrap"
                style={{
                  borderRadius: '12px',
                  background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
                }}
                onClick={downloadProjectSummaryAsPDF}
              >
                Download Your Project Summary As PDF
              </button>
            </div>
            
            {/* Separator after download button */}
            <div className="border-t border-[#E2D5FF] my-8"></div>
          </div>

          {/* What Happens Next */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#6B4EFF" strokeWidth="2"/>
                <polyline points="12,6 12,12 16,14" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3 className="text-xl font-bold text-[#272264]">What Happens Next?</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#272264] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 mt-1">1</div>
                <div>
                  <h4 className="font-semibold text-[#272264] mb-1">Content Creation (1-2 Days)</h4>
                  <p className="text-[#272264]">Our team will create your optimized Amazon content based on your approved sections</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#272264] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 mt-1">2</div>
                <div>
                  <h4 className="font-semibold text-[#272264] mb-1">Quality Review (1 Day)</h4>
                  <p className="text-[#272264]">Internal quality check and optimization for Amazon's guidelines</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#272264] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 mt-1">3</div>
                <div>
                  <h4 className="font-semibold text-[#272264] mb-1">Delivery & Implementation</h4>
                  <p className="text-[#272264]">Our team will create your optimized Amazon content based on your approved sections</p>
                </div>
              </div>
            </div>
            
            {/* Separator after 3 point delivery and implementation */}
            <div className="border-t border-[#E2D5FF] my-8"></div>
          </div>

          {/* Stay In Touch */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3 className="text-xl font-bold text-[#272264]">Stay In Touch</h3>
            </div>
            <p className="text-[#272264] mb-4">
              We'll keep you updated on your project progress and you can reach out anytime with questions or changes.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 flex justify-between items-center">
              <div className="text-[#272264]">
                <span className="font-semibold">Support Email:</span> workinxdigital@gmail.com
              </div>
              <div className="text-[#272264]">
                <span className="font-semibold">Expected Completion:</span> 7-10 business days
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-6 justify-center">
            <button 
              className="px-6 py-3 bg-white text-black font-semibold rounded-lg border border-black hover:bg-gray-50 transition whitespace-nowrap"
              onClick={() => navigate('/listing-onboarding')}
            >
              Go To Dashboard
            </button>
            <button 
              className="px-6 py-3 text-white font-semibold transition whitespace-nowrap"
              style={{
                borderRadius: '12px',
                background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
              }}
              onClick={() => navigate('/listing-onboarding')}
            >
              Start Another Project
            </button>
          </div>
          
          {/* Separator after both buttons */}
          <div className="border-t border-[#E2D5FF] my-8"></div>

          {/* Footer */}
          <div className="text-center text-gray-500 text-sm">
            We're committed to your success on Amazon. Thank you for choosing our services.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionSuccessfulPage; 